/**
 * Identification of the Headless consumer (who embedded CatchUP) from the JWT
 * `consumer` / `consumer_id` claims (contract issued by Wizard.ON, ARTFRAN-15102).
 */

export const KNOWN_CONSUMERS = ['portal'];

export const CONSUMER_UNKNOWN = 'unknown';
export const CONSUMER_UNRECOGNIZED = 'unrecognized';
export const CONSUMER_ID_INVALID = 'invalid';
export const CONSUMER_ID_OTHER = 'other';

// Controlled, low-cardinality consumer ids, optionally suffixed with the issuer environment (e.g. portal-fran-qa)
export const KNOWN_CONSUMER_IDS = ['portal-fran', 'portal-gov'];
export const CONSUMER_ID_ENV_SUFFIXES = ['dev', 'qa', 'stg', 'stage', 'prod'];

const CONSUMER_ID_PATTERN = /^[A-Za-z0-9_\-:.]{1,128}$/;

function isKnownConsumerId(consumerId) {
    return KNOWN_CONSUMER_IDS.some((known) =>
        consumerId === known ||
        CONSUMER_ID_ENV_SUFFIXES.some((suffix) => consumerId === `${known}-${suffix}`)
    );
}
const RAW_MAX_LENGTH = 64;

// Decodes a Base64URL segment as UTF-8 (e.g. accented names)
function base64UrlDecode(value) {
    const base64 = value
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(value.length / 4) * 4, '=');
    const payloadBytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
    if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder().decode(payloadBytes);
    }
    // Fallback for environments without TextDecoder (e.g. older jsdom)
    return decodeURIComponent(Array.from(payloadBytes, (byte) =>
        '%' + ('00' + byte.toString(16)).slice(-2)
    ).join(''));
}

/**
 * Decodes the JWT payload WITHOUT verifying the signature
 * (verification is done by the backend on /wizportal/jwt_validate).
 */
export function decodeJwtPayload(jwtToken) {
    const parts = typeof jwtToken === 'string' ? jwtToken.split('.') : [];
    if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
    }
    try {
        return JSON.parse(base64UrlDecode(parts[1])) || {};
    } catch (_e) {
        throw new Error('Invalid JWT format');
    }
}

export function tryDecodeJwtPayload(jwtToken) {
    try {
        return decodeJwtPayload(jwtToken);
    } catch (_e) {
        return null;
    }
}

function sanitizeRaw(value) {
    return String(value).replace(/[^A-Za-z0-9_\-:.]/g, '_').slice(0, RAW_MAX_LENGTH);
}

/**
 * Resolves the consumer from the JWT payload. Never assumes "portal" when the claim is absent.
 */
export function resolveConsumer(payload) {
    const data = payload || {};
    const result = {consumer: CONSUMER_UNKNOWN, consumerId: CONSUMER_UNKNOWN, consumerRaw: null};

    const rawConsumer = data.consumer;
    const normalized = typeof rawConsumer === 'string'
        ? rawConsumer.trim().toLowerCase()
        : (rawConsumer === undefined || rawConsumer === null ? '' : String(rawConsumer));
    if (normalized !== '') {
        if (KNOWN_CONSUMERS.indexOf(normalized) !== -1) {
            result.consumer = normalized;
        } else {
            result.consumer = CONSUMER_UNRECOGNIZED;
            result.consumerRaw = sanitizeRaw(normalized);
        }
    }

    const rawConsumerId = data.consumer_id;
    if (rawConsumerId !== undefined && rawConsumerId !== null) {
        const consumerId = typeof rawConsumerId === 'number' ? String(rawConsumerId) : rawConsumerId;
        if (typeof consumerId !== 'string' || !CONSUMER_ID_PATTERN.test(consumerId)) {
            result.consumerId = CONSUMER_ID_INVALID;
        } else {
            const normalized = consumerId.toLowerCase();
            result.consumerId = isKnownConsumerId(normalized) ? normalized : CONSUMER_ID_OTHER;
        }
    }

    return result;
}

/**
 * Classifies a Headless authentication failure for observability.
 */
export function classifyAuthError(err, payload) {
    const message = (err && err.message) || '';
    if (!payload || message === 'Invalid JWT format') {
        return 'invalid_format';
    }
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now()) {
        return 'expired';
    }
    if (message.indexOf('Missing required field') === 0) {
        return 'missing_claims';
    }
    return 'invalid';
}
